import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cn } from '../../lib/utils'

/**
 * Tooltip — a short, contextual label shown on hover/focus (Radix-backed), from
 * the Figma DS2026 "Tooltip" specs. This is the *styleable* replacement for the
 * browser's native `title=""` bubble: same delayed-hover behavior, but our dark
 * surface, our timing, keyboard focus support, and `role="tooltip"` for screen
 * readers — none of which the native one lets you control.
 *
 *   <Tooltip>
 *     <TooltipTrigger asChild>
 *       <Button iconOnly aria-label="Info"><Info /></Button>
 *     </TooltipTrigger>
 *     <TooltipContent>Helpful context.</TooltipContent>
 *   </Tooltip>
 *
 * Tokens (Figma → repo): gray-900 surface, white 12px (sm) / 14px (md) text,
 * `rounded-lg`, `shadow-fbui-md`, and a matching gray-900 arrow. Use a tooltip
 * for a *label* (a word or short phrase); for interactive content (buttons,
 * links, forms) reach for `Popover` instead — tooltips vanish on pointer-leave
 * and aren't focusable.
 *
 * A standalone `Tooltip` self-provides its `TooltipProvider`, so a single
 * tooltip works without any app-level setup. When you have many tooltips and
 * want them to share one "first one waits, the rest open instantly" delay, wrap
 * a region in `TooltipProvider` once — the inner `Tooltip`s detect it and defer
 * to that shared provider instead of each making their own.
 */

export interface TooltipProps extends React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Root
> {
  /**
   * Hover delay (ms) before the tooltip opens. Mirrors the native `title`
   * pause. When inside an outer `TooltipProvider`, omit this to inherit the
   * region's shared delay; setting it overrides the region for this one.
   */
  delayDuration?: number
}

// Tracks whether a `TooltipProvider` is above us, so a standalone `Tooltip` can
// self-provide (works with zero setup) while tooltips inside a region defer to
// that region's Provider — which is what enables Radix's shared
// "first one waits, the rest open instantly" grouping.
const HasTooltipProvider = React.createContext(false)

export const Tooltip = ({
  children,
  delayDuration,
  ...props
}: TooltipProps) => {
  const hasProvider = React.useContext(HasTooltipProvider)
  if (hasProvider) {
    // Inherit the region's delay (or override it just for this tooltip).
    return (
      <TooltipPrimitive.Root delayDuration={delayDuration} {...props}>
        {children}
      </TooltipPrimitive.Root>
    )
  }
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration ?? 200}>
      <TooltipPrimitive.Root {...props}>{children}</TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  )
}
Tooltip.displayName = 'Tooltip'

/** Region-level provider — wrap once to share one delay across many tooltips. */
export const TooltipProvider = ({
  delayDuration = 200,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider>) => (
  <HasTooltipProvider.Provider value={true}>
    <TooltipPrimitive.Provider delayDuration={delayDuration} {...props}>
      {children}
    </TooltipPrimitive.Provider>
  </HasTooltipProvider.Provider>
)
TooltipProvider.displayName = 'TooltipProvider'

/** The element that reveals the tooltip on hover/focus. Use `asChild`. */
export const TooltipTrigger = TooltipPrimitive.Trigger

type TooltipSize = 'sm' | 'md'

/** Per-size padding + text (sm = compact label, md = a short sentence). */
const tooltipSize: Record<TooltipSize, string> = {
  sm: 'px-2.5 py-1 text-xs',
  md: 'px-3 py-2 text-sm',
}

export interface TooltipContentProps extends React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
> {
  /** Compact (`sm`) label vs. roomier (`md`) sentence. */
  size?: TooltipSize
  /** Render the little pointer toward the trigger. */
  showArrow?: boolean
}

export const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(
  (
    {
      className,
      size = 'md',
      showArrow = true,
      sideOffset = 6,
      children,
      ...props
    },
    ref
  ) => (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'z-50 max-w-xs rounded-lg bg-fbui-gray-900 font-medium text-fbui-base-white shadow-fbui-md',
          'origin-[--radix-tooltip-content-transform-origin]',
          tooltipSize[size],
          className
        )}
        {...props}
      >
        {children}
        {showArrow && (
          <TooltipPrimitive.Arrow
            width={11}
            height={5}
            className="fill-fbui-gray-900"
          />
        )}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
)
TooltipContent.displayName = TooltipPrimitive.Content.displayName
